import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, Info, Loader2, MoreHorizontal } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { formatDay } from "../lib/finance";
import { cleanBlocks, emptyBlock, insertItem, moveItem, recordsError, unknownVariables, withVariable } from "../lib/records";
import { CLAUSE_GROUPS } from "../lib/clauses";
import { AddBlockMenu, BlockEditor, BlockInserter } from "../components/records/BlockEditors";
import { TemplateOutline, TemplatePreviewFrame } from "../components/records/TemplateBits";
import { ClauseStatus, ThemeSelect } from "./RecordClauses";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

const HISTORY = { created: "Clause créée", updated: "Clause modifiée", draft_saved: "Brouillon enregistré", draft_started: "Nouvelle version commencée",
  published: "Version publiée", draft_discarded: "Brouillon abandonné" };
const VERSION_STATUS = { draft: "brouillon", published: "publiée", deprecated: "remplacée" };

function ClauseRenameDialog({ clause, themes, open, onClose, onDone }) {
  const [name, setName] = useState(clause.name);
  const [theme, setTheme] = useState(clause.theme_code);
  const [description, setDescription] = useState(clause.description || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setName(clause.name); setTheme(clause.theme_code); setDescription(clause.description || ""); setError(""); } }, [open, clause]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/records/clauses/${clause.id}`, { expected_version: clause.version, name: name.trim(), theme_code: theme, description });
      toast.success("Clause modifiée.");
      onDone(data);
    } catch (e) {
      setError(recordsError(e, "Enregistrement impossible."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Nom, thème et description</DialogTitle><DialogDescription>Changer le nom ne modifie ni les modèles ni les documents déjà produits.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="clause-rename">Nom</Label>
            <Input id="clause-rename" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} className="h-10" data-testid="clause-rename" /></div>
          <div className="space-y-1.5"><Label htmlFor="clause-rename-theme">Thème</Label>
            <ThemeSelect themes={themes} value={theme} onChange={setTheme} id="clause-rename-theme" testid="clause-rename-theme" /></div>
          <div className="space-y-1.5"><Label htmlFor="clause-rename-description">Quand l'utiliser</Label>
            <Textarea id="clause-rename-description" value={description} rows={2} maxLength={4000} onChange={(e) => setDescription(e.target.value)} /></div>
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Fermer</Button>
          <Button onClick={submit} disabled={busy || name.trim().length < 2} data-testid="clause-rename-save">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsageCard({ usage }) {
  if (!usage) return <Skeleton className="h-24 w-full" />;
  const { templates, documents, documents_total: total } = usage;
  return (
    <Card className="p-4 space-y-3" data-testid="clause-usage">
      <h2 className="text-sm font-semibold text-slate-900">Utilisée par</h2>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Modèles</p>
        {templates.length === 0 ? <p className="text-sm text-slate-500" data-testid="clause-usage-no-template">Aucun modèle n'utilise cette clause.</p> : (
          <ul className="space-y-1 text-sm text-slate-700">
            {templates.map((u, i) => (
              <li key={i} className="flex flex-wrap items-center gap-x-2" data-testid={`clause-usage-template-${i}`}>
                <Link className="text-jamora hover:underline" to={`/documents/modeles/${u.template_id}`}>{u.template_name}</Link>
                <span className="text-slate-500">version {u.version_number} ({u.status === "draft" ? "brouillon" : "publiée"}) · clause v{u.clause_number}</span>
                {u.update_available && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">mise à jour disponible</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Documents générés</p>
        {total === 0 ? <p className="text-sm text-slate-500">Aucun document que vous pouvez voir ne contient cette clause.</p> : (
          <ul className="space-y-1 text-sm text-slate-700" data-testid="clause-usage-documents">
            {documents.map((d) => (
              <li key={d.id}><Link className="text-jamora hover:underline" to={`/documents/${d.id}`}>{d.number}</Link> · {d.title}{d.clause_number ? ` · clause v${d.clause_number}` : ""}</li>
            ))}
            {total > documents.length && <li className="text-slate-500">… et {total - documents.length} autre{total - documents.length > 1 ? "s" : ""}.</li>}
          </ul>
        )}
      </div>
    </Card>
  );
}

export default function RecordClauseEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [clause, setClause] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [themes, setThemes] = useState([]);
  const [variables, setVariables] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [note, setNote] = useState("");
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [tab, setTab] = useState("edit");
  const [dialog, setDialog] = useState(null);
  const caret = useRef(null);
  const previewSeq = useRef(0);

  const apply = useCallback((data) => {
    setClause(data);
    setBlocks(data.draft ? data.draft.blocks : []);
    setNote(data.draft?.note || "");
  }, []);
  const load = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await api.get(`/records/clauses/${id}`);
      apply(data);
    } catch (e) {
      setLoadError(recordsError(e, "Clause introuvable."));
    }
  }, [id, apply]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/records/template-variables").then(({ data }) => setVariables(data.items)).catch(() => {}); }, []);
  useEffect(() => { api.get("/records/clause-themes").then(({ data }) => setThemes(data.items)).catch(() => {}); }, []);
  const refreshUsage = useCallback(() => { api.get(`/records/clauses/${id}/usage`).then(({ data }) => setUsage(data)).catch(() => setUsage({ templates: [], documents: [], documents_total: 0 })); }, [id]);
  useEffect(() => { refreshUsage(); }, [refreshUsage, clause?.published_number]);

  const draft = clause?.draft;
  const manage = !!clause?.capabilities?.manage;
  const editing = manage && !!draft;
  const cleaned = useMemo(() => cleanBlocks(blocks), [blocks]);
  const dirty = editing && (JSON.stringify(cleaned) !== JSON.stringify(cleanBlocks(draft.blocks)) || note !== (draft.note || ""));
  const unknown = useMemo(() => unknownVariables(blocks, variables), [blocks, variables]);

  useEffect(() => {
    if (!clause || (!editing && !clause.published)) return undefined;
    if (unknown.length) return undefined;
    const mine = ++previewSeq.current;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/records/clauses/${clause.id}/preview`, editing ? { blocks: cleaned, paged: true } : { paged: true });
        if (mine === previewSeq.current) { setPreview(data); setPreviewError(""); }
      } catch (e) {
        if (mine === previewSeq.current) setPreviewError(recordsError(e, "Aperçu indisponible."));
      } finally {
        if (mine === previewSeq.current) setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [clause, editing, cleaned, unknown.length]);

  const run = async (label, request, success) => {
    setBusy(label);
    try {
      const { data } = await request();
      apply(data);
      toast.success(success);
      return data;
    } catch (e) {
      toast.error(recordsError(e, "Action impossible. Réessayez."));
      return null;
    } finally {
      setBusy("");
    }
  };
  const save = () => run("save", () => api.put(`/records/clauses/${id}/draft`, { expected_version: draft.version, blocks: cleaned, note }), "Brouillon enregistré.");
  const publish = async () => {
    let current = clause;
    if (dirty) {
      current = await save();
      if (!current) return;
    }
    await run("publish", () => api.post(`/records/clauses/${id}/publish`, { expected_version: current.draft.version }), "Version publiée : les modèles gardent la version qu'ils ont prise, tant qu'ils ne la mettent pas à jour.");
  };
  const updateBlock = (index, next) => setBlocks((list) => list.map((b, i) => (i === index ? next : b)));
  const addBlock = (kind, at) => {
    caret.current = null;
    setBlocks((list) => insertItem(list, emptyBlock(kind), at));
  };
  const pickIn = (index, key) => {
    const done = withVariable(blocks[index], index, caret.current, key);
    updateBlock(index, done.block);
    caret.current = done.caret;
  };

  if (loadError) {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="clause-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{loadError}</p>
        <Button variant="outline" asChild><Link to="/documents/clauses">Retour aux clauses</Link></Button>
      </Card>
    );
  }
  if (!clause) return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-96 w-full" /></div>;

  const trail = manage ? (
    <div className={editing ? "grid gap-4 md:grid-cols-2" : "space-y-3"}>
      <UsageCard usage={usage} />
      <div className="space-y-4">
        <Card className="p-4 space-y-2" data-testid="clause-versions">
          <h2 className="text-sm font-semibold text-slate-900">Versions</h2>
          <ul className="space-y-1 text-sm text-slate-600">
            {(clause.versions || []).map((v) => (
              <li key={v.id}>Version {v.number} · {VERSION_STATUS[v.status]}{v.published_at ? ` le ${formatDay(v.published_at.slice(0, 10))}${v.published_by_name ? ` par ${v.published_by_name}` : ""}` : ""}{v.note ? ` — ${v.note}` : ""}</li>
            ))}
          </ul>
        </Card>
        <Card className="p-4 space-y-2">
          <h2 className="text-sm font-semibold text-slate-900">Historique</h2>
          <ul className="space-y-1 text-sm text-slate-600">
            {(clause.history || []).slice().reverse().slice(0, 12).map((h, i) => (
              <li key={i}>{HISTORY[h.kind] || h.kind}{h.data?.number ? ` (version ${h.data.number})` : ""} · {h.actor_name} · {new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  ) : null;
  const editor = editing ? (
    <div className="space-y-3" data-testid="clause-editor">
      <p className="flex items-start gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="clause-guidance">
        <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>Les articles se numérotent selon la place de la clause dans chaque contrat : citez le titre d'une clause, jamais un numéro d'article. Les titres, signatures et parties restent ceux du contrat.</span>
      </p>
      {unknown.length > 0 && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="clause-unknown">
          Variable inconnue : {unknown.map((v) => `{${v}}`).join(", ")}. Utilisez « Insérer une variable ».
        </p>
      )}
      {blocks.length === 0 && <p className="text-sm text-slate-500">Le brouillon est vide : ajoutez un article et son texte.</p>}
      <div data-testid="clause-blocks">
      {blocks.map((b, i) => (
        <Fragment key={i}>
          {i > 0 && <BlockInserter index={i} groups={CLAUSE_GROUPS} itemPrefix="clause-add" onAdd={(kind) => addBlock(kind, i)} />}
          <BlockEditor block={b} index={i} count={blocks.length} assets={[]} variables={variables} onPickVariable={(key) => pickIn(i, key)}
            onChange={(next) => updateBlock(i, next)} onCaret={(c) => { caret.current = c; }}
            onMove={(delta) => { caret.current = null; setBlocks((list) => moveItem(list, i, delta)); }}
            onRemove={() => { caret.current = null; setBlocks((list) => list.filter((_, j) => j !== i)); }} />
        </Fragment>
      ))}
      </div>
      <div className="flex justify-center">
        <AddBlockMenu groups={CLAUSE_GROUPS} itemPrefix="clause-add" testid="clause-add-block" onAdd={(kind) => addBlock(kind)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="clause-note">Note de version</Label>
        <Input id="clause-note" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} placeholder="Ce qui change dans cette version" className="h-10" />
      </div>
    </div>
  ) : (
    <div className="space-y-3">
      <Card className="p-4 text-sm text-slate-600 space-y-2" data-testid="clause-readonly">
        {manage ? (
          <>
            <p>La version {clause.published_number} est publiée et ne se modifie plus. Pour changer le texte, commencez une nouvelle version : elle repart de celle-ci, et les modèles qui utilisent la clause gardent la version qu'ils ont prise.</p>
            <Button size="sm" onClick={() => run("draft", () => api.post(`/records/clauses/${id}/draft`), "Nouvelle version commencée.")} disabled={!!busy} data-testid="clause-new-version">
              {busy === "draft" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Nouvelle version
            </Button>
          </>
        ) : <p>Version {clause.published_number} publiée{clause.published?.published_at ? ` le ${formatDay(clause.published.published_at.slice(0, 10))}` : ""}.</p>}
      </Card>
      <TemplateOutline blocks={clause.published?.blocks} previewTestid="clause-preview" title="Plan de la clause" onBeforeGo={() => setTab("preview")} />
      {trail}
    </div>
  );
  const previewPane = (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900">Aperçu{editing ? " du brouillon" : ""}</h2>
        <span className="text-xs text-slate-500">Habillée de la charte, dans un contrat d'exemple.</span>
      </div>
      <TemplatePreviewFrame preview={preview} loading={previewLoading} error={previewError} testid="clause-preview" maxHeightClassName="lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto" />
    </div>
  );

  return (
    <div className="space-y-4 pb-4" data-testid="clause-page">
      <Link to="/documents/clauses" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft size={14} />Clauses</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h1 className="font-display font-bold text-2xl text-slate-900 break-words">{clause.name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span className="rounded-full bg-jamora-50 px-2 py-0.5 text-xs font-medium text-jamora" data-testid="clause-theme-label">{clause.theme_label}</span>
            <ClauseStatus clause={clause} />
            {editing && <span>Brouillon de la version {draft.number}{dirty ? " · modifications non enregistrées" : ""}</span>}
          </div>
          {clause.description && <p className="text-sm text-slate-500">{clause.description}</p>}
        </div>
        {manage && (
          <div className="flex flex-wrap items-center gap-2">
            {editing && (
              <>
                <Button variant="outline" onClick={save} disabled={!dirty || !!busy || unknown.length > 0} data-testid="clause-save">{busy === "save" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button>
                <Button onClick={publish} disabled={!!busy || unknown.length > 0} data-testid="clause-publish">{busy === "publish" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Publier</Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="Autres actions" data-testid="clause-more"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" title={clause.name}>
                <DropdownMenuItem onClick={() => setDialog("rename")}>Nom, thème et description</DropdownMenuItem>
                {!clause.published_number && hasPermission(user, "records:delete") && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="clause-delete">Supprimer la clause…</DropdownMenuItem>}
                {editing && clause.published_number && <DropdownMenuItem onClick={() => setDialog("discard")} data-testid="clause-discard">Abandonner le brouillon</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => run("active", () => api.patch(`/records/clauses/${id}`, { expected_version: clause.version, active: !clause.active }),
                  clause.active ? "Clause désactivée : elle n'est plus proposée aux modèles." : "Clause réactivée.")} data-testid="clause-toggle">
                  {clause.active ? "Désactiver la clause" : "Réactiver la clause"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div className="flex gap-1 lg:hidden" role="tablist" aria-label="Modifier ou voir l'aperçu">
        {[["edit", editing ? "Modifier" : "Version"], ["preview", "Aperçu"]].map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={tab === value} onClick={() => setTab(value)} data-testid={`clause-tab-${value}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === value ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{label}</button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className={`min-w-0 ${tab === "edit" ? "" : "hidden lg:block"}`}>{editor}</div>
        <div className={`min-w-0 lg:sticky lg:top-4 ${tab === "preview" ? "" : "hidden lg:block"}`}>{previewPane}</div>
      </div>

      {editing && trail}

      <ClauseRenameDialog clause={clause} themes={themes} open={dialog === "rename"} onClose={() => setDialog(null)} onDone={(data) => { setDialog(null); apply(data); }} />
      <DeleteWithReasonDialog
        target={dialog === "delete" ? clause : null} testId="delete-clause" onClose={() => setDialog(null)}
        title={`Supprimer la clause « ${clause?.name} » ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une clause déjà publiée, ou citée par un modèle, se désactive à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/records/clauses/${id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/documents/clauses");
          } catch (e) {
            toast.error(recordsError(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
      <AlertDialog open={dialog === "discard"} onOpenChange={(v) => !v && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abandonner le brouillon de la version {draft?.number} ?</AlertDialogTitle>
            <AlertDialogDescription>Ses modifications sont perdues ; la version {clause.published_number} reste publiée.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Garder le brouillon</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="clause-discard-confirm"
              onClick={() => { setDialog(null); run("discard", () => api.delete(`/records/clauses/${id}/draft`), "Brouillon abandonné."); }}>Abandonner</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
